import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { history, getLocale, useSelector } from 'umi';
import { omit } from 'lodash-es';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import RegionSelector from '@intelligence/components/RegionSelector';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import SelectChoice from '@/pages/intelligence/components/SelectCompoenet/SelectChoice';
import IconDom from '@/pages/intelligence/components/SelectCompoenet/ItemDom/IconDom';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import { getDefaultTime } from '@/pages/intelligence/utils/topchartsUtils';
import { getItemFromArray } from '@/pages/intelligence/utils/tableUtils';
import { updateDefaultTimeParams } from '@/pages/intelligence/pages/TopCharts/_config/detailDefaltfilterTime';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import useEarliestTime from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/useEarliestTime';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  DETAIL_PAGEID,
  getDetailPageCfg,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import CombineContent from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/components/StatsContext';
import { getDefaultPageSortAttr } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_utils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import useChartDownloadHooks from '@/pages/intelligence/pages/TopCharts/_hooks/useChartDownloadHooks';
import { pcconsoleStats } from '@intelligence/v3/constants/downloadLogPoint';
import { buildOrderMetricsFromPageMetricValues } from '@intelligence/components/DownloadCommon/DownloadPage/utils/reorderCombineDetailDownloadMetrics';
import {
  getTopList,
  getFixedTableData,
  MarketsType,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/countryUtils';
import { normalizeCountryMarketRows } from '@/pages/intelligence/utils/commonUtils';
import type { IRegion } from '@home/types/common';
import PlayerbyCountryCountries from '@/pages/intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/SteamSales/components/PlayerbyCountry/Countries';
import { buildTopCountriesBarChartData } from '@/pages/intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/SteamSales/_config/_utils';
import ConsoleDataAdapter from './consoleDataAdapter';
import {
  CONSOLE_DATA_SEGMENT,
  getConsoleDataMetricsByDateType,
  getConsoleDataPeriodFromFilterValue,
  isConsoleDataPlayersByCountryMetric,
  mergeConsoleDataFilterValue,
  resolveConsoleDataMetric,
} from './_config';
import {
  createMultiValueLineSeriesFormatter,
  expandMetricOptionsWithMultiValue,
  isMultiValuePriceMetric,
} from '@intelligence/pages/TopCharts/utils/multiValueMetricUtils';
import { mergeSmallPlayerCountriesIntoOthers } from './_playerCountryUtils';

const filterRegions = (
  regions?: IRegion[] | null,
  playerCountries?: any[] | null,
): IRegion[] => {
  if (!Array.isArray(regions) || regions.length === 0) return [];
  if (!Array.isArray(playerCountries) || playerCountries.length === 0) {
    return [];
  }

  const playerCountryCodes = playerCountries
    .map((playerCountry) => playerCountry?.market?.toLowerCase() || '')
    .filter((marketCode) => !!marketCode);

  return regions.reduce<IRegion[]>((acc, region) => {
    const markets = region?.country_set?.filter((market) => {
      const countryCode = market?.country_code || '';
      return !!countryCode && playerCountryCodes.includes(countryCode?.toLowerCase());
    });

    return !Array.isArray(markets) || markets.length === 0
      ? acc
      : [...acc, { ...region, country_set: markets }];
  }, []);
};

const ConsoleDataView: React.FC<any> = (props) => {
  const {
    filterCfg,
    subSegment,
    setActiveSegment,
    gameId,
    activeId,
    moduleName,
    isBasicLoading,
    loading,
    setLoading,
    tabType,
    entityType,
    basicInfo,
    appIds,
    isTwoMonths,
    gsdDevicesFlatten,
    gsdSegments,
    mainSegment,
    regionsCountries,
    filterLeftComp,
  } = props;
  const { formatMessage, isCN } = useIntlExtend();
  const locale = getLocale();
  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});

  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue || {};
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});
  const [filterValue, setFilterVale] = useState(() =>
    mergeConsoleDataFilterValue(
      DEFAULT_FORM_VALUE,
      isOperateDetail ? detailFilterExtend : undefined,
    ),
  );
  const [fetchParams, setFetchParams] = useState<Record<string, any>>({});
  const isStatsPage = tabType === 'stats';
  const isCountriesPage = tabType === 'countries';
  const resolvedFilterCfg = useMemo(() => {
    if (!isCountriesPage || !filterCfg) {
      return filterCfg;
    }
    const filterKeyList = (filterCfg.filterKeyList || []).filter(
      (key: string) => key !== filterKeyMap.PLATFORMCASCADER,
    );
    return {
      ...filterCfg,
      filterKeyList,
    };
  }, [filterCfg, isCountriesPage]);
  const [metric, setMetric] = useState<string>(() =>
    resolveConsoleDataMetric(
      subSegment === CONSOLE_DATA_SEGMENT ? '' : subSegment,
      '',
      isCountriesPage,
    ),
  );
  const [metricSegVal, setMetricSegVal] = useState(metric);
  const [trendLineList, setTrendLineList] = useState<any[]>([]);
  const [trendTableList, setTrendTableList] = useState<any[]>([]);
  const [trendChartLoading, setTrendChartLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(loading);
  const [trendChartType, setTrendChartType] = useState<string>(ChartType.Line);
  const [trendError, setTrendError] = useState(false);
  const [playerCountryData, setPlayerCountryData] = useState<any[]>([]);
  const [playerCountryBarData, setPlayerCountryBarData] = useState<any[]>([]);
  const [countryLatestDate, setCountryLatestDate] = useState('');
  const [countryLineLoading, setCountryLineLoading] = useState(true);
  const [marketChartType, setMarketChartType] = useState<string>(ChartType.Bar);
  const [topList, setTopList] = useState<any>({});
  const [marketFixedTableData, setMarketFixedTableData] = useState<any[]>([]);
  const [hideGlobal, setHideGlobal] = useState(false);
  const [filterData, setFilterData] = useState<any>({
    tableList: [],
    barList: [],
  });
  const [regionSelectValue, setRegionSelectValue] = useState({
    regions: [],
    markets: ['global'],
    type: 'market',
  });
  const [marketType] = useState(MarketsType.Global);
  const requestSeqRef = useRef(0);
  const isPlayerbyCountry = isConsoleDataPlayersByCountryMetric(metric);
  const pageId = `consoleData—${metric}`;

  const updatePageLoading = useCallback(
    (value: boolean) => {
      if (typeof setLoading === 'function') {
        setLoading(value);
      }
    },
    [setLoading],
  );

  const pageCfg = getDetailPageCfg(DETAIL_PAGEID.consoleData, tabType, entityType);
  const playerByCountryPageCfg = getDetailPageCfg(
    DETAIL_PAGEID.consoleDataPlayersByCountry,
    'countries',
    entityType,
  );
  const trendPageCfg = useMemo(() => {
    if (!isCountriesPage || isPlayerbyCountry) {
      return pageCfg;
    }
    return {
      ...pageCfg,
      defaultChartType: ChartType.Line,
      disableChartType: [ChartType.StackedLine, ChartType.Bar, ChartType.Pie],
    };
  }, [isCountriesPage, isPlayerbyCountry, pageCfg]);
  const defaultByDeviceVal = pageCfg?.defaultByDeviceVal || undefined;
  const infoItem = getItemFromArray({
    list: getConsoleDataMetricsByDateType(
      fetchParams.period || fetchParams.date_type,
      isCountriesPage,
    ),
    key: 'value',
    keyVal: metric,
  });

  const metricsList = useMemo(
    () =>
      getConsoleDataMetricsByDateType(
        fetchParams.period || fetchParams.date_type,
        isCountriesPage,
      ),
    [fetchParams.date_type, fetchParams.period, isCountriesPage],
  );

  const metricOptions = useMemo(
    () =>
      metricsList.map((item: any) => ({
        value: item.value,
        text: (
          <IconDom
            data={{
              ...item,
              text: formatMessage({ id: item.text }),
              svg: item?.icon ? getDataSourceIcon(item.icon) : undefined,
            }}
          />
        ),
      })),
    [formatMessage, metricsList],
  );

  const tableMetricList = useMemo(() => {
    if (!infoItem) return [];
    const baseItem = {
      ...infoItem,
      text: infoItem.text,
      textStr: formatMessage({ id: infoItem.text }),
    };
    return expandMetricOptionsWithMultiValue(
      [baseItem],
      trendTableList,
      formatMessage,
    );
  }, [formatMessage, infoItem, trendTableList, locale]);

  const consoleLineSeriesFormatter = useMemo(
    () =>
      createMultiValueLineSeriesFormatter({
        metric,
        infoItem,
        trendLineList,
        formatMessage,
        defaultFormatter: (params) => trendPageCfg?.lineSeriesFormatter?.(params),
        multiMetricFormatter: (params) =>
          getDetailPageCfg(
            DETAIL_PAGEID.allMetrics,
            isCountriesPage ? 'countries' : 'stats',
            entityType,
          )?.lineSeriesFormatter?.(params),
      }),
    [
      metric,
      infoItem,
      formatMessage,
      trendLineList,
      trendPageCfg,
      isCountriesPage,
      entityType,
    ],
  );

  const { chartDownloadConfig, showDropdownBtnListCur, setTrendsChartDownList } =
    useChartDownloadHooks({
      locale,
      metricValue: metric,
      entityType,
      activeSegment: metric,
    });

  const {
    earliestPeriod,
    formattedEarliestDay,
    updateEarliestDateData,
    baseReleaseTimeValue,
    baseReleaseTimeData,
  } = useEarliestTime({
    entityType,
    basicInfo,
    appIds,
    dateType: fetchParams.period || fetchParams.date_type,
    topchartsState,
  });

  const buildBaseFetchParams = useCallback(
    (params: Record<string, any> = {}): Record<string, any> => ({
      unified_ids: [gameId],
      edition_id: gameId,
      entity_type: entityType,
      regions: ['global'],
      region_type: 'market',
      data_sources: ['alinea'],
      metrics: [params.segment || metric],
      metric: params.segment || metric,
      ...params,
    }),
    [entityType, gameId, metric],
  );

  const normalizePlayerCountryMarkets = useCallback(
    (list: any[]) => normalizeCountryMarketRows(list, regionsCountries),
    [regionsCountries],
  );

  const getTrends = useCallback(
    async (params: Record<string, any>) => {
      const requestId = ++requestSeqRef.current;
      setTrendLoading(true);
      updatePageLoading(true);
      setTrendTableList([]);
      const period =
        params.period ||
        params.date_type ||
        getConsoleDataPeriodFromFilterValue(filterValue);
      const newFetchParams = buildBaseFetchParams({
        ...fetchParams,
        ...params,
        ...(period ? { period, date_type: period } : {}),
      });

      try {
        const trendRes = await ConsoleDataAdapter.fetchTrend(newFetchParams);
        if (requestId !== requestSeqRef.current) {
          return { start_date: '', end_date: '' };
        }

        const startDate = trendRes?.req?.start_date;
        const endDate = trendRes?.req?.end_date;
        const lineList = trendRes?.data?.trend || [];
        const lineListTemp = lineList.sort(
          (a: any, b: any) => moment(a.date).valueOf() - moment(b.date).valueOf(),
        );

        setFetchParams((prev) => ({
          ...prev,
          ...newFetchParams,
          start_date: startDate,
          end_date: endDate,
        }));
        setTrendLineList(lineListTemp);
        setTrendsChartDownList(lineListTemp);
        setTrendTableList(lineList);
        setTrendError(false);
        return { start_date: startDate, end_date: endDate };
      } catch (error) {
        if (requestId !== requestSeqRef.current) {
          return { start_date: '', end_date: '' };
        }
        setTrendLineList([]);
        setTrendTableList([]);
        setTrendError(true);
        return { start_date: '', end_date: '' };
      } finally {
        if (requestId === requestSeqRef.current) {
          setTrendLoading(false);
          updatePageLoading(false);
          setTrendChartLoading(false);
        }
      }
    },
    [buildBaseFetchParams, fetchParams, filterValue, setTrendsChartDownList, updatePageLoading],
  );

  const getCountry = useCallback(
    async (params: Record<string, any> = {}) => {
      const requestId = ++requestSeqRef.current;
      const activeMetric = params.segment || params.metric || metric;
      const period =
        params.period ||
        params.date_type ||
        fetchParams.period ||
        fetchParams.date_type ||
        getConsoleDataPeriodFromFilterValue(filterValue);
      const newFetchParams = buildBaseFetchParams({
        ...fetchParams,
        ...params,
        ...(period ? { period, date_type: period } : {}),
        segment: activeMetric,
        metrics: [activeMetric],
        metric: activeMetric,
        start_date: '',
        end_date: '',
      });
      setFetchParams(newFetchParams);
      setTrendLoading(true);
      updatePageLoading(true);
      setCountryLineLoading(true);

      try {
        const countryRes = await ConsoleDataAdapter.fetchMarket(newFetchParams);
        if (requestId !== requestSeqRef.current) {
          return;
        }

        const tableList = normalizePlayerCountryMarkets(
          countryRes?.data?.players_country_table || [],
        );
        const tableBarList = normalizePlayerCountryMarkets(
          countryRes?.data?.players_country_charts || [],
        );

        if (countryRes?.code === 0 && !!countryRes?.data) {
          const topListTmp = getTopList(tableList);
          const topListBarTmp = getTopList(tableBarList);
          const firstFixedData = !tableList.length
            ? []
            : getFixedTableData(marketType, topListTmp);
          const mergedTableList = mergeSmallPlayerCountriesIntoOthers(
            topListTmp?.list,
            activeMetric,
          );
          const mergedBarList = buildTopCountriesBarChartData(
            mergeSmallPlayerCountriesIntoOthers(topListBarTmp?.list, activeMetric),
            activeMetric,
          );

          setPlayerCountryData(mergedTableList);
          setPlayerCountryBarData(mergedBarList);
          setFilterData({
            tableList: mergedTableList,
            barList: mergedBarList,
          });
          setCountryLatestDate(countryRes?.data?.time);
          setTopList(topListTmp);
          setMarketFixedTableData(firstFixedData);
          setTrendError(false);
        } else {
          setPlayerCountryData([]);
          setPlayerCountryBarData([]);
          setFilterData({ tableList: [], barList: [] });
          setTrendError(true);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) {
          return;
        }
        setPlayerCountryData([]);
        setPlayerCountryBarData([]);
        setFilterData({ tableList: [], barList: [] });
        setTrendError(true);
      } finally {
        if (requestId === requestSeqRef.current) {
          setTrendLoading(false);
          updatePageLoading(false);
          setCountryLineLoading(false);
        }
      }
    },
    [
      buildBaseFetchParams,
      fetchParams,
      filterValue,
      marketType,
      metric,
      normalizePlayerCountryMarkets,
      updatePageLoading,
    ],
  );

  const getStatsData = useCallback(
    async (params: Record<string, any> = {}) => {
      const mergedParams: Record<string, any> = buildBaseFetchParams({
        ...fetchParams,
        ...params,
      });
      try {
        const { start_date: startDate, end_date: endDate } = await getTrends(mergedParams);
        let nextParams: Record<string, any> = { ...mergedParams };
        if (startDate && endDate) {
          nextParams.start_date = startDate;
          nextParams.end_date = endDate;
        } else {
          const [defaultStart, defaultEnd] = getDefaultTime(
            mergedParams.period || mergedParams.date_type,
            isTwoMonths,
          );
          nextParams.start_date = defaultStart;
          nextParams.end_date = defaultEnd;
        }
        nextParams = updateDefaultTimeParams({
          fetchParams: nextParams,
          startDate: nextParams.start_date,
          endDate: nextParams.end_date,
          period: mergedParams.period || mergedParams.date_type,
          isTwoMonths,
        });
        setFetchParams(nextParams);
      } catch (error) {
        setTrendLoading(false);
        updatePageLoading(false);
      }
    },
    [buildBaseFetchParams, fetchParams, getTrends, isTwoMonths, updatePageLoading],
  );

  const getPageData = useCallback(
    (params: Record<string, any> = {}) => {
      const activeMetric = params.segment || params.metric || metric;
      if (
        isConsoleDataPlayersByCountryMetric(activeMetric) &&
        isCountriesPage
      ) {
        getCountry(params);
        return;
      }
      getStatsData(params);
    },
    [getCountry, getStatsData, isCountriesPage, metric],
  );

  const changeUrl = useCallback(
    (activeSubTab: string) => {
      const newPath = `/intelligence/topCharts/${moduleName || 'pcConsoleGames'}/gamesDetails/${gameId}/${activeId}/${activeSubTab}`;
      if (window.location.pathname !== newPath) {
        history.replace(newPath);
      }
    },
    [activeId, gameId, moduleName],
  );

  const handleMetricChange = useCallback(
    (value: string) => {
      ++requestSeqRef.current;
      setMetric(value);
      setMetricSegVal(value);
      setActiveSegment?.(value);
      changeUrl(value);
      if (isConsoleDataPlayersByCountryMetric(value)) {
        setMarketChartType(ChartType.Bar);
        setCountryLineLoading(true);
      } else {
        setTrendChartType(ChartType.Line);
        setTrendChartLoading(true);
        setTrendTableList([]);
        setTrendLineList([]);
      }
      setTrendLoading(true);
      updatePageLoading(true);
      const period =
        fetchParams.period ||
        fetchParams.date_type ||
        getConsoleDataPeriodFromFilterValue(filterValue);
      const nextParams = buildBaseFetchParams({
        ...fetchParams,
        ...(period ? { period, date_type: period } : {}),
        segment: value,
        metrics: [value],
        metric: value,
      });
      setFetchParams((prev) => ({
        ...prev,
        ...nextParams,
      }));
      getPageData(nextParams);
    },
    [
      buildBaseFetchParams,
      changeUrl,
      fetchParams,
      filterValue,
      getPageData,
      setActiveSegment,
      updatePageLoading,
    ],
  );

  const handleConfirm = (value: any) => {
    const period =
      value?.period ??
      value?.date_type ??
      fetchParams.period ??
      fetchParams.date_type ??
      getConsoleDataPeriodFromFilterValue(filterValue);
    const nextMetric = resolveConsoleDataMetric(metric, period, isCountriesPage);
    const newFetchParams = buildBaseFetchParams({
      ...fetchParams,
      ...value,
      ...(period ? { period, date_type: period } : {}),
      segment: nextMetric,
      metrics: [nextMetric],
      metric: nextMetric,
    });

    if (nextMetric !== metric) {
      setMetric(nextMetric);
      setMetricSegVal(nextMetric);
      setActiveSegment?.(nextMetric);
      changeUrl(nextMetric);
    }

    getPageData(newFetchParams);
    updateEarliestDateData({
      dataSources: ['alinea'],
      metrics: [nextMetric],
      regionType: 'market',
      regions: ['global'],
      dateType: period,
    });
  };

  const getDownloadFetchParams = () => {
    const activeMetric = fetchParams.segment || metric;
    const downloadParams = buildBaseFetchParams({
      ...fetchParams,
      segment: activeMetric,
      metrics: [activeMetric],
      metric: activeMetric,
    });
    const sourceMetricsMap = { alinea: [activeMetric] };

    if (!gameId || !activeMetric) {
      return {
        ...downloadParams,
        source_metrics: sourceMetricsMap,
        metrics: [activeMetric],
        order_metrics: buildOrderMetricsFromPageMetricValues(
          [activeMetric],
          sourceMetricsMap,
        ),
      };
    }

    const removeKeys = [
      'b_true_data',
      'b_group_by_date',
      'b_growth_ratio',
      'b_sum_avg_show',
      'b_group_by_market',
      'b_group_by_region',
      'b_group_by_platform',
      'b_group_by_segment',
      'start_release_time',
      'end_release_time',
    ];
    const convertedParams = omit(
      isConsoleDataPlayersByCountryMetric(activeMetric)
        ? ConsoleDataAdapter.convertMarketParams(downloadParams)
        : ConsoleDataAdapter.convertParams(downloadParams),
      removeKeys,
    );

    return {
      ...downloadParams,
      ...convertedParams,
      source: 'alinea',
      subSegment: activeMetric,
      currentMetric: 'consoleData',
      source_metrics: sourceMetricsMap,
      metrics: [activeMetric],
      order_metrics: buildOrderMetricsFromPageMetricValues(
        [activeMetric],
        sourceMetricsMap,
      ),
      unified_ids: convertedParams.ids || [gameId],
      regions: convertedParams.markets,
      isCountriesPage,
    };
  };

  const regionsNew = useMemo(
    () => filterRegions(regionsCountries, playerCountryData),
    [regionsCountries, playerCountryData],
  );

  const filterTableData = (list: any[], markets: string[]) => {
    if (!Array.isArray(list) || !markets?.length) return list;
    return list.filter((item) => markets.includes(item?.market));
  };

  const filterBarData = (tableList: any[], markets: string[]) => {
    const filtered = filterTableData(tableList, markets);
    return buildTopCountriesBarChartData(
      mergeSmallPlayerCountriesIntoOthers(filtered, metric),
      metric,
    );
  };

  const hanldeRegionSelect = (val: any) => {
    setRegionSelectValue(val);
    const { realMarkets, markets } = val || {};
    const needFilterData = realMarkets?.length && !markets.includes('global');
    setFilterData({
      tableList: needFilterData
        ? filterTableData(playerCountryData, markets)
        : playerCountryData,
      barList: needFilterData
        ? filterBarData(playerCountryData, markets)
        : playerCountryBarData,
    });
    setHideGlobal(needFilterData);
  };

  const pageSortAttr = getDefaultPageSortAttr(isStatsPage, 'desc', metric);
  const isStepLine =
    isMultiValuePriceMetric(metric) || metric?.endsWith('__est_price');
  const cardTitle =
    infoItem?.text && !isPlayerbyCountry
      ? `${formatMessage({ id: infoItem.text })}${
          locale === 'zh-CN' ? '' : ' '
        }${formatMessage({ id: 'dbi_detail_trend' })}`
      : '';

  const getCardStatus = () => {
    if (isPlayerbyCountry) {
      const isPageLoading = loading || trendLoading || countryLineLoading;
      const hasTable = filterData.tableList?.length > 0;
      const hasBar = filterData.barList?.length > 0;
      return {
        isLoading: isPageLoading,
        isError: trendError && !isPageLoading,
        isNoData: !isPageLoading && !hasTable && !hasBar,
      };
    }
    const isPageLoading = loading || trendLoading;
    return {
      isLoading: isPageLoading,
      isError: trendError && !isPageLoading,
      isNoData: !isPageLoading && !trendTableList?.length,
    };
  };

  const showTimePlatformFilter = !isPlayerbyCountry;

  useEffect(() => {
    if (isBasicLoading) {
      updatePageLoading(true);
      return;
    }

    const { filterValue: nextFilterValue, filterParams } = getDbiFilterParams({
      filterSaveData: undefined,
      filterExtend: detailFilterExtend,
      specialParams: resolvedFilterCfg?.specialParams,
      isOperate: isOperateDetail,
      filterName: '',
      filterKeyList: resolvedFilterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });
    if (isOperateDetail) {
      setDetailFilterExtend(nextFilterValue);
    }
    const mergedFilterValue = mergeConsoleDataFilterValue(
      DEFAULT_FORM_VALUE,
      nextFilterValue,
    );
    setFilterVale(mergedFilterValue);

    const apiParams =
      resolvedFilterCfg?.handleFilterToApiParams?.(filterParams, mergedFilterValue) ||
      filterParams;
    const period =
      apiParams?.period ??
      apiParams?.date_type ??
      getConsoleDataPeriodFromFilterValue(mergedFilterValue);
    const resolvedMetric = resolveConsoleDataMetric(
      subSegment === CONSOLE_DATA_SEGMENT ? '' : subSegment,
      period,
      isCountriesPage,
    );
    setMetric(resolvedMetric);
    setMetricSegVal(resolvedMetric);

    const nextParams = buildBaseFetchParams({
      ...apiParams,
      ...(period ? { period, date_type: period } : {}),
      segment: resolvedMetric,
      metrics: [resolvedMetric],
      metric: resolvedMetric,
    });

    getPageData(nextParams);
    updateEarliestDateData({
      dataSources: ['alinea'],
      metrics: [resolvedMetric],
      regionType: 'market',
      regions: ['global'],
      dateType: period,
    });
  }, [isBasicLoading, pageId, tabType]);

  useEffect(() => {
    const period = fetchParams.period || fetchParams.date_type;
    const resolvedMetric = resolveConsoleDataMetric(
      subSegment === CONSOLE_DATA_SEGMENT ? '' : subSegment,
      period,
      isCountriesPage,
    );
    if (resolvedMetric !== metric) {
      setMetric(resolvedMetric);
      setMetricSegVal(resolvedMetric);
      setFetchParams((prev) => ({
        ...prev,
        segment: resolvedMetric,
        metrics: [resolvedMetric],
        metric: resolvedMetric,
      }));
    }
  }, [subSegment, fetchParams.date_type, fetchParams.period, isCountriesPage]);

  useEffect(() => {
    setMetricSegVal(metric);
  }, [metric]);

  useEffect(() => {
    if (isPlayerbyCountry) {
      setMarketChartType(ChartType.Bar);
      setRegionSelectValue({
        regions: [],
        markets: ['global'],
        type: 'market',
      });
      setHideGlobal(false);
    } else {
      setTrendChartType(ChartType.Line);
    }
  }, [isPlayerbyCountry]);

  return (
    <>
      <div className="dbi-statsfilter-warp dbi-console-data-statsfilter">
        <div className="statsfilter-warp-left">
          {filterLeftComp || (
            <SelectChoice
              className="dbi-stats-select-item"
              labelValue={formatMessage({ id: 'dbi_label_metrics' })}
              changeValue={metric}
              optionsList={metricOptions}
              getSelectMultiple={handleMetricChange}
              listWidth={isCN ? 282 : 310}
            />
          )}
          {isPlayerbyCountry && isCountriesPage ? (
            <RegionSelector
              key="region"
              locale={locale}
              disableNoChina={true}
              defaultValue={{
                regions: [],
                markets: ['global'],
                type: 'market',
              }}
              loading={loading}
              onConfirm={hanldeRegionSelect}
              options={regionsNew}
              value={regionSelectValue}
              entityType={entityType}
              showRadioBubble={true}
            />
          ) : null}
          {showTimePlatformFilter ? (
            <>
              <Filter
                timeFilterValues={{
                  type:
                    fetchParams.period ||
                    fetchParams.date_type ||
                    getConsoleDataPeriodFromFilterValue(filterValue),
                  value: [fetchParams.start_date, fetchParams.end_date],
                }}
                filterConfirm={handleConfirm}
                filterReset={() => {}}
                sinceReleaseTime={baseReleaseTimeValue}
                earliestDataTime={earliestPeriod}
                filterValue={filterValue}
                setFilterVale={setFilterVale}
                filterCfg={resolvedFilterCfg}
                defaultValue={DEFAULT_FORM_VALUE}
              />
              {isCountriesPage ? (
                <span className="dbi-statsfilter-warp-left-text">
                  {formatMessage({ id: 'dbi_info_only_global_data' })}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="dbi-statsfilter-warp-right">
          <ExportTable
            entityType={entityType}
            tableParams={getDownloadFetchParams()}
            isCoutriesPage={isCountriesPage}
            isDisableDownload={loading || !gameId || !metric}
            isShowMaxDownloadRowsTips={false}
            downloadModalSize={820}
            isDisableDataAmount={true}
            newStyleFileName={`${basicInfo?.name}${
              isStatsPage ? '_Stats' : isCountriesPage ? '_Countries' : ''
            }`}
            gsdDevicesFlatten={gsdDevicesFlatten}
            gsdSegments={gsdSegments}
            showDropdownBtnList={showDropdownBtnListCur}
            chartDownloadConfig={chartDownloadConfig}
            tabType={tabType}
            useWebDownload={entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE}
            businessType="combine-detail-consoledata"
            downloadKey="combineStatsSteamData"
            downloadLogConfigKey={
              pcconsoleStats?.[`${entityType}_${tabType}_consoleData`]
            }
          />
        </div>
      </div>
      {isPlayerbyCountry && isCountriesPage ? (
        <PlayerbyCountryCountries
          pageId={pageId}
          tableData={filterData.tableList}
          playerCountryBarData={filterData.barList}
          cardStatus={getCardStatus()}
          chartType={marketChartType}
          setChartType={setMarketChartType}
          chartLoading={countryLineLoading}
          setChartLoading={setCountryLineLoading}
          chartSegment={metric}
          dateType={fetchParams.period || fetchParams?.date_type}
          countryLatestDate={countryLatestDate}
          regionsCountries={regionsCountries || []}
          topList={topList}
          fixedTableData={hideGlobal ? [] : marketFixedTableData}
          infoItem={infoItem}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          pageCfg={{
            ...(playerByCountryPageCfg || {}),
            tableMetricList,
          }}
          entityType={entityType}
        />
      ) : (
        <CombineContent
          pageId={pageId}
          entityType={entityType}
          isCountriesPage={false}
          fixedTableData={[]}
          tableData={trendTableList}
          sumAvgMaxList={[]}
          lineData={trendLineList}
          barData={[]}
          pieData={[]}
          stackBarData={[]}
          chartType={trendChartType}
          setChartType={setTrendChartType}
          metricSegVal={metricSegVal}
          setMetricSegVal={setMetricSegVal}
          cardStatus={getCardStatus()}
          earliestDay={formattedEarliestDay}
          baseReleaseTime={baseReleaseTimeData}
          firstWeekReleaseTime={baseReleaseTimeValue}
          earliestPeriod={earliestPeriod}
          defaultByDeviceVal={defaultByDeviceVal}
          dateType={fetchParams.period || fetchParams?.date_type}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          pageSortAttr={pageSortAttr}
          pageCfg={{
            ...(trendPageCfg || {}),
            tableMetricList,
            lineSeriesFormatter: consoleLineSeriesFormatter,
          }}
          cardTitle={cardTitle}
          chartLoading={trendChartLoading}
          setChartLoading={setTrendChartLoading}
          visualisMetric={metric}
          visualisTitleNow={formatMessage({ id: infoItem?.text || '' })}
          isStepLine={isStepLine}
          gameName={basicInfo?.name}
          gameCover={basicInfo?.cover}
          regionsCountries={regionsCountries || []}
          isPercentage={infoItem?.renderType === RenderType.GROWTH_ONLY}
          noDataMessage={formatMessage({ id: 'common_nodata' })}
          mainSegment={mainSegment}
          ifLineChartLegendOn={showDropdownBtnListCur}
        />
      )}
    </>
  );
};

export default ConsoleDataView;
